/**
 * Role visibility for module navigation items.
 *
 * Lives outside Navigation.tsx so the rules that decide what each role sees in
 * the top bar can be tested without rendering the whole nav, and so a module's
 * nav definition can be checked against them (ISS-440).
 */
import type { NavItem } from '../../contexts/ModuleContext';
import type { PageAccess } from '../../modules/pageAccess';

export interface NavRoleContext {
  readonly isAdmin: boolean;
  readonly isManagerOrAdmin: boolean;
  readonly hasRole: (role: string) => boolean;
  /** users.isExternal — an outside contractor. Absent means staff. */
  readonly isExternal?: boolean;
  /** May this viewer open a page with this access? Absent = refuse every `requirePage` item (fail closed). */
  readonly canOpenPage?: (access: PageAccess) => boolean;
  /** users.role as stored, for items gated by an `allowRole` predicate. */
  readonly role?: string;
}

/** True when this single item's own gates admit the viewer. */
export function canSeeNavItem(item: NavItem, ctx: NavRoleContext): boolean {
  if (item.requireManagerOrAdmin && !ctx.isManagerOrAdmin) return false;
  if (item.requireAdmin && !ctx.isAdmin) return false;
  if (item.hideForExternal && ctx.isExternal) return false;
  if (item.requirePage && !(ctx.canOpenPage?.(item.requirePage) ?? false)) return false;
  if (item.requiredRoles && !item.requiredRoles.some((role) => ctx.hasRole(role))) {
    return false;
  }
  if (item.allowRole && !item.allowRole(ctx.role)) return false;
  return true;
}

/**
 * Filter a module's nav for one viewer. Children are filtered too, and a group
 * left with no children is dropped rather than lingering as an empty dropdown.
 */
export function visibleNavItems(items: NavItem[], ctx: NavRoleContext): NavItem[] {
  return items
    .filter((item) => canSeeNavItem(item, ctx))
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((child) => canSeeNavItem(child, ctx)) }
        : item
    )
    .filter((item) => !item.children || item.children.length > 0);
}
